// StudentsPanel — list, add, and edit students.
// Add Student only requires Name, Class and Guardian to save; admission no.,
// DOB, blood group, address etc. live behind "More details" since most
// admissions don't need them on day one. The guardian can be created inline
// (GuardianPicker) instead of requiring a separate trip to the Guardians
// tab first. Route/pickup assignment lives on the Transport tab, not here —
// not every student rides the bus, so it shouldn't gate adding one.
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import {
  getStudents, createStudent, updateStudent, deleteStudent,
  getGuardians, getClasses, admitEnquiry, getStudentsByStatus,
} from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { inputCls, Field, Msg, ClassSelect, errText } from '../ui/Form';
import GuardianPicker, { GeneratedPasswordBanner } from './GuardianPicker';

const EMPTY = {
  name: '', class_id: '', roll_number: '', guardian_id: '',
  admission_no: '', date_of_birth: '', gender: '', father_name: '', mother_name: '', address: '', blood_group: '', category: '',
  date_of_admission: '',
};

export default function StudentsPanel({ onOpenStudent, editId, onEditHandled, admitFrom, onAdmitHandled, onImport }) {
  const [students,  setStudents]  = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [classes,   setClasses]   = useState([]);
  const [form,      setForm]      = useState(EMPTY);
  const [guardianObj, setGuardianObj] = useState(null);
  const [justCreatedGuardian, setJustCreatedGuardian] = useState(null);
  const [moreOpen,  setMoreOpen]  = useState(false);
  const [sameAsFather, setSameAsFather] = useState(false);
  const [sameAsMother, setSameAsMother] = useState(false);
  const [editing,   setEditing]   = useState(null);
  // Preserved untouched across edits to this form — Route/Pickup now live on
  // the Transport tab; wiping them here would silently un-assign a student
  // from their bus every time an admin fixed a typo in, say, blood group.
  const [keepTransport, setKeepTransport] = useState({ route_id: null, stop_order: null });
  const [msg,       setMsg]      = useState('');
  const [filter,    setFilter]    = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const formRef = useRef(null);
  const [enquiryId, setEnquiryId] = useState(null); // set while adding a student from an admission enquiry

  // "Admit" on an enquiry lands here with its details to pre-fill a new student.
  useEffect(() => {
    if (!admitFrom) return;
    setEditing(null);
    setForm({ ...EMPTY, name: admitFrom.student_name || '' });
    setGuardianObj(null); setJustCreatedGuardian(null); setKeepTransport({ route_id: null, stop_order: null });
    setEnquiryId(admitFrom.id);
    setMsg(`Adding ${admitFrom.student_name} from an admission enquiry — pick their class and guardian, then Add.`);
    onAdmitHandled?.();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [admitFrom]);

  useEffect(() => { load(); }, [statusFilter]);

  // "Edit details" on a student profile lands here with that student's id.
  useEffect(() => {
    if (!editId || !students.length) return;
    const st = students.find(x => x.id === editId);
    if (st) startEdit(st);
    onEditHandled?.();
  }, [editId, students]);

  async function load() {
    try {
      const [s, g, c] = await Promise.all([statusFilter === 'active' ? getStudents() : getStudentsByStatus(statusFilter), getGuardians(), getClasses()]);
      setStudents(s); setGuardians(g); setClasses(c);
    } catch { setMsg('Failed to load data'); }
  }

  function startEdit(student) {
    setEditing(student.id);
    setJustCreatedGuardian(null);
    setSameAsFather(false); setSameAsMother(false);
    setGuardianObj(student.guardian_id ? guardians.find(g => g.id === student.guardian_id) || { id: student.guardian_id, name: student.guardian_name, phone: student.guardian_phone } : null);
    setKeepTransport({ route_id: student.route_id || null, stop_order: student.stop_order || null });
    setForm({
      name:        student.name,
      class_id:    student.class_id    || '',
      guardian_id: student.guardian_id || '',
      roll_number: student.roll_number || '',
      admission_no:  student.admission_no  || '',
      date_of_birth: student.date_of_birth || '',
      gender:        student.gender        || '',
      father_name:   student.father_name   || '',
      mother_name:   student.mother_name   || '',
      address:       student.address       || '',
      blood_group:   student.blood_group   || '',
      category:      student.category      || '',
      date_of_admission: student.date_of_admission || '',
    });
    setMoreOpen(!!(student.admission_no || student.date_of_birth || student.gender || student.father_name || student.mother_name || student.address || student.blood_group || student.category));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function cancel() {
    setEditing(null); setForm(EMPTY); setMsg(''); setEnquiryId(null);
    // justCreatedGuardian is left alone here on purpose — if a guardian was
    // just created inline but the student add is then cancelled, that
    // guardian (and its one-time password) still exists in the database;
    // only the explicit dismiss button on the banner, or creating another
    // new guardian, should clear it.
    setGuardianObj(null); setMoreOpen(false);
    setSameAsFather(false); setSameAsMother(false);
    setKeepTransport({ route_id: null, stop_order: null });
  }

  function pickGuardian(g) {
    setGuardianObj(g);
    setForm(f => ({ ...f, guardian_id: g ? g.id : '' }));
    if (!g) return;
    if (sameAsFather) setForm(f => ({ ...f, father_name: g.name }));
    if (sameAsMother) setForm(f => ({ ...f, mother_name: g.name }));
  }

  function toggleSameAs(which) {
    if (which === 'father') {
      const next = !sameAsFather; setSameAsFather(next);
      if (next && guardianObj) setForm(f => ({ ...f, father_name: guardianObj.name }));
    } else {
      const next = !sameAsMother; setSameAsMother(next);
      if (next && guardianObj) setForm(f => ({ ...f, mother_name: guardianObj.name }));
    }
  }

  async function save(e) {
    e.preventDefault();
    const className = classes.find(c => String(c.id) === String(form.class_id))?.name || '';
    const payload = {
      ...form,
      class:       className, // legacy free-text column some older reports still fall back to — kept in sync, never shown
      guardian_id: form.guardian_id || null,
      class_id:    form.class_id    || null,
      roll_number: form.roll_number || null,
      route_id:    keepTransport.route_id,
      stop_order:  keepTransport.stop_order,
    };
    try {
      const saved = editing ? await updateStudent(editing, payload) : await createStudent(payload);
      let note = editing ? 'Student updated ✅' : 'Student added ✅';
      if (!editing && enquiryId) {
        await admitEnquiry(enquiryId, saved.id);
        note = 'Student added and the enquiry marked admitted ✅';
      }
      cancel(); setMsg(note); load();
    } catch (err) {
      setMsg(errText(err));
    }
  }

  async function remove(student) {
    if (!window.confirm(
      `Permanently delete ${student.name}? This also erases their attendance, fees, payments, documents and all other records. This cannot be undone.`
    )) return;
    try {
      await deleteStudent(student.id);
      if (editing === student.id) cancel();
      setMsg(`${student.name} deleted`);
      load();
    } catch (err) {
      setMsg(errText(err));
    }
  }

  const q = filter.trim().toLowerCase();
  const visible = students.filter(s =>
    (!classFilter || String(s.class_id) === classFilter) &&
    (!q || [s.name, s.roll_number, s.admission_no, s.guardian_name, s.guardian_phone]
      .some(v => v && String(v).toLowerCase().includes(q)))
  );

  const canSave = form.name.trim() && form.class_id && form.guardian_id;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Students</h2>
        {onImport && <Button variant="secondary" onClick={onImport}>Import from Excel</Button>}
      </div>

      <div ref={formRef} />
      <Card className="p-5 mb-6 max-w-[520px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Student' : 'Add Student'}</h3>

        {justCreatedGuardian && <GeneratedPasswordBanner guardian={justCreatedGuardian} onDismiss={() => setJustCreatedGuardian(null)} />}

        <form onSubmit={save} className="flex flex-col gap-3">
          <Field label="Full Name">
            <input required className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Class">
              <ClassSelect classes={classes} value={form.class_id} onChange={v => setForm({...form, class_id: v})} required />
            </Field>
            <Field label="Roll Number">
              <input className={inputCls} value={form.roll_number} onChange={e => setForm({...form, roll_number: e.target.value})} placeholder="e.g. 12" />
            </Field>
          </div>

          <Field label="Guardian">
            <GuardianPicker value={guardianObj} onChange={pickGuardian} onCreated={setJustCreatedGuardian} />
          </Field>

          <button type="button" onClick={() => setMoreOpen(!moreOpen)}
            className="flex items-center gap-1.5 text-xs font-bold text-ink-soft hover:text-ink self-start mt-1">
            {moreOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            More details (admission no., DOB, blood group, address…) — optional
          </button>

          {moreOpen && (
            <div className="grid grid-cols-2 gap-3 border-t border-line-soft pt-3">
              <Field label="Admission / SR No.">
                <input className={inputCls} value={form.admission_no} onChange={e => setForm({...form, admission_no: e.target.value})} />
              </Field>
              <Field label="Date of Birth">
                <input type="date" className={inputCls} value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} />
              </Field>
              <Field label="Gender">
                <select className={inputCls} value={form.gender} onChange={e => setForm({...form, gender: e.target.value})}>
                  <option value="">—</option><option>Female</option><option>Male</option><option>Other</option>
                </select>
              </Field>
              <Field label="Blood Group">
                <select className={inputCls} value={form.blood_group} onChange={e => setForm({...form, blood_group: e.target.value})}>
                  <option value="">—</option>
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(b => <option key={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Date of Admission">
                <input type="date" className={inputCls} value={form.date_of_admission} onChange={e => setForm({...form, date_of_admission: e.target.value})} />
              </Field>
              <Field label="Category (for UDISE+ returns)">
                <select className={inputCls} value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="">—</option>
                  {['General', 'OBC', 'SC', 'ST', 'EWS'].map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Father's Name" className="col-span-2">
                <div className="flex gap-2 items-center mt-1">
                  <input className={`${inputCls} flex-1`} value={form.father_name} onChange={e => setForm({...form, father_name: e.target.value})} />
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-soft whitespace-nowrap">
                    <input type="checkbox" checked={sameAsFather} onChange={() => toggleSameAs('father')} className="w-3.5 h-3.5 accent-[var(--transit)]" />
                    Same as guardian
                  </label>
                </div>
              </Field>
              <Field label="Mother's Name" className="col-span-2">
                <div className="flex gap-2 items-center mt-1">
                  <input className={`${inputCls} flex-1`} value={form.mother_name} onChange={e => setForm({...form, mother_name: e.target.value})} />
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-soft whitespace-nowrap">
                    <input type="checkbox" checked={sameAsMother} onChange={() => toggleSameAs('mother')} className="w-3.5 h-3.5 accent-[var(--transit)]" />
                    Same as guardian
                  </label>
                </div>
              </Field>
              <Field label="Address" className="col-span-2">
                <input className={inputCls} value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </Field>
            </div>
          )}

          <div className="flex gap-2 mt-1">
            <Button type="submit" disabled={!canSave}>{editing ? 'Update' : 'Add'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          <Msg msg={msg} />
        </form>
      </Card>

      <Card>
        <CardHeader title={`Students (${visible.length}${visible.length !== students.length ? ` of ${students.length}` : ''})`}
          action={
            <div className="flex gap-2">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Status"
                className="border border-line rounded-s px-2 py-1.5 text-sm bg-paper-raised text-ink">
                <option value="active">Studying</option><option value="alumni">Alumni</option><option value="left">Left (TC issued)</option><option value="all">Everyone</option>
              </select>
              <select value={classFilter} onChange={e => setClassFilter(e.target.value)} aria-label="Filter by class"
                className="border border-line rounded-s px-2 py-1.5 text-sm bg-paper-raised text-ink">
                <option value="">All classes</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by name, roll, guardian…" aria-label="Filter students"
                className="border border-line rounded-s px-3 py-1.5 text-sm bg-paper-raised text-ink w-56 focus:outline-none focus:ring-2 focus:ring-transit" />
            </div>
          } />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">ID</th><th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Roll #</th>
                <th className="px-4 py-2.5">Guardian</th><th className="px-4 py-2.5">Route</th>
                <th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(s => (
                <tr key={s.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{s.id}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => onOpenStudent(s.id)} className="font-semibold text-transit-deep hover:underline text-left">{s.name}</button>
                    {s.admission_no && <span className="block text-xs text-ink-soft">Adm. {s.admission_no}</span>}
                  </td>
                  <td className="px-4 py-3">{s.class_name || '—'}</td>
                  <td className="px-4 py-3 tabular-nums">{s.roll_number || '—'}</td>
                  <td className="px-4 py-3">{s.guardian_name || '—'}</td>
                  <td className="px-4 py-3">{s.route_name || <span className="text-ink-soft">Not on transport</span>}</td>
                  <td className="px-4 py-3 flex gap-1.5">
                    <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(s)}>Edit</Button>
                    <button onClick={() => remove(s)} aria-label={`Delete ${s.name}`}
                      className="text-ink-soft hover:text-critical p-1.5" title="Delete student">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-ink-soft">
                  {students.length ? 'No students match the filter' : 'No students yet — add one above'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
